import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { Spinner } from "../components/common";

export default function PaymentReturn({ cancelled }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [amount, setAmount] = useState(null);
  const [kind, setKind] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        setAmount(data.amount_display);
        setKind(data.kind);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch {}
      attempts.current += 1;
      if (attempts.current > 10) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, cancelled]);

  const isGuard = kind === "guard_subscription";
  const isBadge = kind === "badge_subscription";
  const cfg = {
    checking: { icon: <Spinner className="w-10 h-10 text-primary" />, title: "Confirming payment", body: "Please wait while we confirm your payment…", tint: "text-primary" },
    success: { icon: <CheckCircle2 size={48} className="text-success" />, title: isGuard ? "Account Guard is on" : isBadge ? "Verified badge active" : "Payment successful", body: isGuard ? "Your 24/7 AI fraud monitoring is now active." : isBadge ? "Your verified badge is now on your account." : `${amount || ""} was added to your balance.`, tint: "text-success" },
    failed: { icon: <XCircle size={48} className="text-destructive" />, title: "Payment failed", body: "Your payment did not go through. No money was added.", tint: "text-destructive" },
    cancelled: { icon: <XCircle size={48} className="text-muted-foreground" />, title: "Payment cancelled", body: "You cancelled the payment. Nothing was charged.", tint: "text-muted-foreground" },
    timeout: { icon: <Clock size={48} className="text-warning" />, title: "Still processing", body: "This is taking longer than usual. Check back in a moment.", tint: "text-warning" },
  }[state];

  return (
    <div className="min-h-screen w-full flex justify-center bg-background">
      <div className="w-full max-w-md bg-mesh min-h-screen px-6 pt-10 pb-10 flex flex-col">
        <Logo size={34} />
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="grid place-items-center w-20 h-20 rounded-full bg-card border border-border mb-5">{cfg.icon}</div>
          <h1 className="font-display text-2xl font-semibold">{cfg.title}</h1>
          <p className="text-muted-foreground mt-2 max-w-xs">{cfg.body}</p>
        </div>
        <div className="space-y-3">
          <button data-testid="payment-home-btn" onClick={() => nav(state === "success" && isGuard ? "/app/guard" : state === "success" && isBadge ? "/app/badge" : "/app")} className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold">{state === "success" && isGuard ? "Open Account Guard" : state === "success" && isBadge ? "View my badge" : "Go to dashboard"}</button>
          {state !== "success" && <button data-testid="payment-retry-btn" onClick={() => nav(isGuard ? "/app/guard" : isBadge ? "/app/badge" : "/app/add-money")} className="w-full h-14 rounded-2xl bg-secondary font-semibold">Try again</button>}
        </div>
      </div>
    </div>
  );
}
